var _ = require('underscore');
var TorsoView = require('backbone-torso/modules/View');
var eregWorkflowConfigService = require('./eregWorkflowConfigService');
var wizardUtils = require('./wizardUtils');

/**
 * This view renders the wizard navigation bar. It is meant as a temporary solution for embedding torso pages in the
 * existing VCC wizard. Once all prereg functionality is ported to Torso, wizard should be replaced with a 'Hub and Spoke' model.
 * Don't instantiate this View directly. Use a WizardWidget.
 *
 * Interactions with this view should be managed through WizardWidget (except attaching) and should not be done directly.
 *
 * @class WizardNavView
 * @constructor
 * @param options {Object} Options to configure this view
 *   @param options.patientVecnaId {String} the vecna id of the patient being displayed
 *   @param options.appointmentExternalId {String} the external id of the appointment being displayed
 *   @param options.regDate {String} the date of the appointments being displayed
 *   @param options.template {Handlebars.Template} the template to use when rendering this view.
 * @extends Torso.View
 */
module.exports = TorsoView.extend({
  tagName: 'ol',
  className: 'brec-steps',

  /**
   * Sets the current page of the wizard to the page described by the context.
   * @method navigateToWizardPage
   * @param currentPageName {String} the name of the top-level wizard step.
   */
  navigateToWizardPage: function(currentPageName) {
    this.set('currentPageName', currentPageName);
  },

  /**
   * Sets the wizard data retrieved from the server.
   * @method setWizardData
   * @param wizardData {Object} wizard data retrieved from the server
   *   @param wizardData.wizardSteps {Array} an array of the steps in the wizard
   *     @param wizardData.wizardSteps.name {String} the steps name
   *     @param wizardData.wizardSteps.status {String} the steps status ('NEW' or 'DONE')
   */
  setWizardData: function(wizardData) {
    this.set('wizardData', wizardData);
  },

  /**
   * @method initialize
   * @override
   * @param options {Object} Options to configure this view
   *   @param options.patientVecnaId {String} the vecna id of the patient being displayed
   *   @param options.appointmentExternalId {String} the external id of the appointment being displayed
   *   @param options.regDate {String} the date of the appointments being displayed
   *   @param options.template {Handlebars.Template} the template to use when rendering this view.
   */
  initialize: function(options) {
    this.template = options.template;

    this._patientVecnaId = options.patientVecnaId;
    this._appointmentExternalId = options.appointmentExternalId;
    this._regDate = options.regDate;

    var wizardId = this._getWizardId();
    this._baseRegUrl = wizardUtils.generateBaseRegUrl(wizardId);

    this.listenTo(this.viewState, 'change:wizardData change:currentPageName', this._updateWizardSteps);
    this.listenTo(this.viewState, 'change:wizardSteps change:numSteps', this.render);
  },

  prepareFields: {
    baseRegUrl: '_baseRegUrl'
  },

  /**
   * Parses the wizard data retrieved from the server into a context that is useful for rendering the wizard.
   * @method _updateWizardSteps
   * @private
   */
  _updateWizardSteps: function() {
    var wizardData = this.get('wizardData');
    if (!wizardData) {
      return;
    }
    var currentPageName = this.get('currentPageName');

    var wizardSteps = {};
    var available = true;
    var numSteps = 0;

    var thisView = this;
    var enabledSteps = _.filter(wizardData.wizardSteps, function(step) {
      return thisView._isEnabledStep(step.name);
    });
    _.each(enabledSteps, function(wizardStep, stepIndex) {
      var isCurrentStep = (wizardStep.name === currentPageName);
      var isCompletedStep = (wizardStep.status === 'DONE');
      var isFirstStep = (stepIndex === 0);
      var nextStepAvailable = isCompletedStep;

      numSteps++;
      wizardSteps[wizardStep.name] = {
        isFirst: isFirstStep,
        available: available,
        isCurrent: isCurrentStep,
        isCompleted: isCompletedStep
      };
      available = nextStepAvailable;
    });

    this.set({
      numSteps: numSteps,
      wizardSteps: wizardSteps
    });
  },

  /**
   * @method _isEnabledStep
   * @param topLevelStepName {String} name of step to find
   * @return {Boolean} true if step enabled, false if disabled
   * @private
   */
  _isEnabledStep: function(topLevelStepName) {
    var wizardId = this._getWizardId();
    var disabledSteps = eregWorkflowConfigService.getDisabledWizardSteps(wizardId);
    return disabledSteps[topLevelStepName] !== true;
  },

  /**
   * Generate the identifier for the current wizard (patient id, appt id, reg date).
   * @method _getWizardId
   * @return {{patientVecnaId: *, appointmentExternalId: *, regDate: *}}
   * @private
   */
  _getWizardId: function() {
    return {
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: this._appointmentExternalId,
      regDate: this._regDate
    };
  }
});
